import { convertHeicToWebp } from "./convertHeicToWebp";
import { SVG_WATERMARK } from "./watermark"


// Size constants
const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB
export const FILE_MAX_SIZE_SELECTION_MB = 100 // 100MB max file size for selection
const TARGET_COMPRESS_SIZE_MB = 4 // 4MB target size after compression

// Types
export interface MediaDimensions {
  width: number
  height: number
  size: number
}

export interface ValidationResult {
  valid: boolean
  error?: string
}

export interface MediaUploadResult {
  success: boolean
  filename: string
  publicUrl: string
  dimensions?: MediaDimensions
}

export interface MediaUploadOptions {
  uploadType?: "avatar" | "gallery" | "story" | "video"
  maxSizeMB?: number
  description?: string
  addWatermark?: boolean
}

export interface MediaTypeInfo {
  mimeType: string
  extensions: string[]
  convertToWebp?: boolean
}

const IMAGE_TYPES: MediaTypeInfo[] = [
  { mimeType: "image/jpeg", extensions: ["jpg", "jpeg", "jpe"], convertToWebp: true },
  { mimeType: "image/png", extensions: ["png"], convertToWebp: true },
  { mimeType: "image/webp", extensions: ["webp"], convertToWebp: false },
  // { mimeType: "image/gif", extensions: ["gif"], convertToWebp: false },
  // { mimeType: "image/svg+xml", extensions: ["svg"], convertToWebp: false },
  // { mimeType: "image/tiff", extensions: ["tiff", "tif"], convertToWebp: true },
  // { mimeType: "image/jfif", extensions: ["jfif"], convertToWebp: true },
  // { mimeType: "image/bmp", extensions: ["bmp"], convertToWebp: true },
  // { mimeType: "image/heif", extensions: ["heif"], convertToWebp: true },
  { mimeType: "image/heic", extensions: ["heic"], convertToWebp: true },
]

const VIDEO_TYPES: MediaTypeInfo[] = [
  { mimeType: "video/mp4", extensions: ["mp4"] },
  { mimeType: "video/quicktime", extensions: ["mov", "qt"] },
  // { mimeType: "video/webm", extensions: ["webm"] },
  // { mimeType: "video/ogg", extensions: ["ogv"] },
  // { mimeType: "video/x-flv", extensions: ["flv"] },
  // { mimeType: "video/x-matroska", extensions: ["mkv"] },
  // { mimeType: "video/3gpp", extensions: ["3gp"] },
  // { mimeType: "video/hevc", extensions: ["hevc"] },
]

// Derived arrays for backward compatibility
export const ALLOWED_IMAGE_TYPES = IMAGE_TYPES.map((type) => type.mimeType)
export const ALLOWED_VIDEO_TYPES = VIDEO_TYPES.map((type) => type.mimeType)

// Helper function to get all image extensions
export const IMAGE_EXTENSIONS = IMAGE_TYPES.flatMap((type) => type.extensions)

// Helper function to get all video extensions
export const VIDEO_EXTENSIONS = VIDEO_TYPES.flatMap((type) => type.extensions)

// Helper function to get media type info by extension
export function getMediaTypeInfoByExtension(extension: string): MediaTypeInfo | undefined {
  const normalizedExt = extension.toLowerCase().trim()
  return [...IMAGE_TYPES, ...VIDEO_TYPES].find((type) => type.extensions.includes(normalizedExt))
}

// Helper function to get media type info by MIME type
export function getMediaTypeInfoByMimeType(mimeType: string): MediaTypeInfo | undefined {
  return [...IMAGE_TYPES, ...VIDEO_TYPES].find((type) => type.mimeType === mimeType)
}

// Validation functions
export function isValidMediaSize(file: File, maxSize: number = MAX_FILE_SIZE): ValidationResult {
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Arquivo muito grande. O tamanho máximo é ${Math.round(maxSize / (1024 * 1024))}MB.`,
    }
  }
  return { valid: true }
}

export function isValidMediaType(file: File): ValidationResult {
  const isHeic = file.name.toLowerCase().endsWith(".heic") || file.type === "image/heic" || file.type === "image/heif"

  const isImage = ALLOWED_IMAGE_TYPES.includes(file.type) || isHeic
  const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type)

  if (!isImage && !isVideo && file.type !== "") {
    return {
      valid: false,
      error: `${file.name} | ${file.type} | Tipo de arquivo não suportado. Use ${ALLOWED_IMAGE_TYPES.join(", ")} ou ${ALLOWED_VIDEO_TYPES.join(", ")}.`,
    }
  }

  return { valid: true }
}

// Helper function to create an Image from a File
async function createImageFromFile(file: File): Promise<HTMLImageElement> {
  const reader = new FileReader()

  const dataUrl = await new Promise<string>((resolve, reject) => {
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error("Failed to read file"))
    reader.readAsDataURL(file)
  })

  const img = new Image()
  img.src = dataUrl
  img.crossOrigin = "anonymous"

  await img.decode()
  return img
}

// Function to convert canvas to File
function canvasToFile(canvas: HTMLCanvasElement, filename: string, type = "image/webp", quality = 0.9): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to create blob from canvas"))

        const file = new File([blob], filename, {
          type,
          lastModified: Date.now(),
        })
        resolve(file)
      },
      type,
      quality,
    )
  })
}

// 1. Resize function
export async function getMediaResized(file: File, maxDimension = 2400): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file // Only resize images
  }

  try {
    const img = await createImageFromFile(file)

    let width = img.width
    let height = img.height

    // Resize image to max dimensions while maintaining aspect ratio
    if (width > maxDimension || height > maxDimension) {
      const ratio = Math.min(maxDimension / width, maxDimension / height)
      width = Math.floor(width * ratio)
      height = Math.floor(height * ratio)
    } else {
      // No resize needed
      return file
    }

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")

    if (!ctx) throw new Error("Failed to get canvas context")

    // Draw the original image
    ctx.drawImage(img, 0, 0, width, height)

    // Convert canvas to file
    return await canvasToFile(canvas, file.name, "image/webp")
  } catch (error) {
    console.error("Error resizing media:", error)
    return file // Return original file if resize fails
  }
}

// 2. Watermark function
export async function getMediaWatermarked(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file // Only watermark images
  }

  try {
    const img = await createImageFromFile(file)

    // Load watermark
    const watermarkImg = await new Promise<HTMLImageElement>((resolve, reject) => {
      const watermarkImg = new Image()
      watermarkImg.src = `data:image/svg+xml;utf8,${encodeURIComponent(SVG_WATERMARK)}`
      watermarkImg.crossOrigin = "anonymous"
      watermarkImg.onload = () => resolve(watermarkImg)
      watermarkImg.onerror = () => reject(new Error("Failed to load watermark"))
    })

    const canvas = document.createElement("canvas")
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext("2d")

    if (!ctx) throw new Error("Failed to get canvas context")

    // Draw the original image
    ctx.drawImage(img, 0, 0, img.width, img.height)

    // Add watermark
    const watermarkWidth = Math.floor(img.width * 0.6)
    const watermarkHeight = Math.floor((watermarkWidth * watermarkImg.height) / watermarkImg.width)

    // Center the watermark both horizontally and vertically
    const watermarkX = Math.floor((img.width - watermarkWidth) / 2)
    const watermarkY = Math.floor((img.height - watermarkHeight) / 2)

    ctx.drawImage(watermarkImg, watermarkX, watermarkY, watermarkWidth, watermarkHeight)

    // Convert canvas to file
    return await canvasToFile(canvas, file.name, "image/webp")
  } catch (error) {
    console.error("Error adding watermark:", error)
    return file // Return original file if watermarking fails
  }
}

// 3. Compression function
export async function getMediaCompressed(file: File, maxSizeMB = TARGET_COMPRESS_SIZE_MB): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file // Only compress images
  }

  // If file is already smaller than the max size, return it
  if (file.size <= maxSizeMB * 1024 * 1024) {
    return file
  }

  try {
    // Extract EXIF orientation
    let orientation = 1 // Default orientation (no rotation)
    const exifr = await import("exifr")
    try {
      const arrayBuffer = await file.arrayBuffer()
      const exif = await exifr.parse(arrayBuffer);
      orientation = exif?.Orientation || 1;
      orientation = exif?.Orientation || 1
    } catch (err) {
      console.warn("Failed to read EXIF orientation during compression:", err)
    }

    // Use browser-image-compression library for better handling of orientation
    const imageCompression = await import("browser-image-compression")

    const options = {
      maxSizeMB,
      useWebWorker: true,
      maxWidthOrHeight: 1920, // Reasonable max size
      preserveExif: true,
      exifOrientation: orientation, // Use the orientation we found
    }

    // Compress the image
    return await imageCompression.default(file, options)
  } catch (error) {
    console.error("Error compressing media:", error)
    return file // Return original file if compression fails
  }
}

// Combined processing function with dimensions
export async function mediaProcessing(
  file: File,
  userId: string,
  maxSizeMB = TARGET_COMPRESS_SIZE_MB,
): Promise<{ file: File; dimensions: MediaDimensions; valid: boolean; error?: string }> {
  try {
    // Validate file type
    const typeValidation = isValidMediaType(file)
    if (!typeValidation.valid) {
      return {
        file: file,
        dimensions: await getMediaDimensions(file),
        valid: false,
        error: typeValidation.error,
      }
    }

    // Validate file size
    const sizeValidation = isValidMediaSize(file, maxSizeMB * 1024 * 1024)
    if (!sizeValidation.valid) {
      return {
        file: file,
        dimensions: await getMediaDimensions(file),
        valid: false,
        error: sizeValidation.error,
      }
    }

    let processedFile = file

    // Apply each step in sequence
    processedFile = await getMediaResized(processedFile)
    processedFile = await getMediaWatermarked(processedFile)
    processedFile = await getMediaCompressed(processedFile, maxSizeMB)

    // Get dimensions of the processed file
    const dimensions = await getMediaDimensions(processedFile)

    return {
      file: processedFile,
      dimensions,
      valid: true,
    }
  } catch (error) {
    console.error("Error in media processing:", error)
    // Return original file and its dimensions if processing fails
    const dimensions = await getMediaDimensions(file)
    return {
      file: file,
      dimensions,
      valid: false,
      error: error instanceof Error ? error.message : "Unknown error during media processing",
    }
  }
}

// Media dimensions functions
export async function getImageDimensions(file: File): Promise<MediaDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
        size: file.size,
      })
      URL.revokeObjectURL(img.src)
    }
    img.onerror = () => {
      reject(new Error("Failed to load image"))
      URL.revokeObjectURL(img.src)
    }
    img.src = URL.createObjectURL(file)
  })
}

export async function getVideoDimensions(file: File): Promise<MediaDimensions> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video")
    video.preload = "metadata"

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
      })
      URL.revokeObjectURL(video.src)
    }

    video.onerror = () => {
      reject(new Error("Failed to load video metadata"))
      URL.revokeObjectURL(video.src)
    }

    video.src = URL.createObjectURL(file)
  })
}

export async function getMediaDimensions(file: File): Promise<MediaDimensions> {
  if (file.type.startsWith("image/")) {
    return getImageDimensions(file)
  } else if (file.type.startsWith("video/")) {
    return getVideoDimensions(file)
  } else {
    return {
      width: 0,
      height: 0,
      size: file.size,
    }
  }
}

// File preparation functions
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

// Modified createPreviewUrl function with media processing capabilities
export interface PreviewResult {
  url: string
  processedFile?: File
  dimensions?: MediaDimensions
  valid?: boolean
  error?: string
}

export async function getPreviewUrlWithProcessing(
  file: File,
  options?: {
    process?: boolean
    userId?: string
    maxSizeMB?: number
    addWatermark?: boolean
  },
): Promise<PreviewResult> {
  const { process = false, userId, maxSizeMB = TARGET_COMPRESS_SIZE_MB, addWatermark = true } = options || {}

  // Always create the preview URL for the original file
  const url = URL.createObjectURL(file)

  // If processing is not required, return just the URL
  if (!process || !userId) {
    return { url }
  }

  try {
    // Check if file is HEIC/HEIF
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.type === "image/heic" || file.type === "image/heif"

    let processedFile = file

    // Convert HEIC to WebP if needed
    if (isHeic) {
      processedFile = await convertHeicToWebp(file)
    }

    // Use the updated mediaProcessing function to get both the processed file and dimensions
    const {
      file: finalProcessedFile,
      dimensions,
      valid,
      error,
    } = await mediaProcessing(processedFile, userId, maxSizeMB)

    return {
      url,
      processedFile: finalProcessedFile,
      dimensions,
      valid,
      error,
    }
  } catch (error) {
    console.error("Error processing media during preview:", error)
    // Return just the URL if processing fails
    return {
      url,
      valid: false,
      error: error instanceof Error ? error.message : "Unknown error during preview processing",
    }
  }
}

// For backward compatibility - simple version that just returns the URL string
export function getPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}



// Update the prepareFileForUpload function to handle validation results
export async function prepareFileForUpload(
  fileOrResult: File | PreviewResult,
  userId: string,
  options: { maxSizeMB?: number; addWatermark?: boolean } = {},
): Promise<{ file: File; dimensions: MediaDimensions; valid: boolean; error?: string }> {
  const { maxSizeMB = TARGET_COMPRESS_SIZE_MB, addWatermark = true } = options

  // If we already have a processed file from createPreviewUrl with validation results
  if ("processedFile" in fileOrResult && fileOrResult.processedFile && fileOrResult.dimensions) {
    return {
      file: fileOrResult.processedFile,
      dimensions: fileOrResult.dimensions,
      valid: fileOrResult.valid ?? true,
      error: fileOrResult.error,
    }
  }

  // Handle the case where it's a PreviewResult but doesn't have processedFile
  if ("url" in fileOrResult) {
    // If it's a PreviewResult but doesn't have a processedFile, throw an error
    if (!fileOrResult.processedFile) {
      throw new Error("Invalid file provided to prepareFileForUpload: No processedFile available")
    }

    // Use the updated mediaProcessing function with the processed file
    return await mediaProcessing(fileOrResult.processedFile, userId, maxSizeMB)
  }

  // It's a File, use it directly
  return await mediaProcessing(fileOrResult, userId, maxSizeMB)
}

// Upload functions
export async function getMediaUploadAuth(
  file: File,
  userId: string,
): Promise<{ success: boolean; filename: string; uploadUrl: string; key: string }> {
  try {
    // Import the server action dynamically to avoid issues with 'use server' directive
    const { getSignedUploadUrl } = await import("@/app/actions/cdnActions")

    // Call the server action to get a signed upload URL
    const result = await getSignedUploadUrl(userId, file.name, file.type)

    if (!result.success) {
      throw new Error("Failed to get upload URL")
    }

    return result
  } catch (error) {
    console.error("Upload authorization failed:", error)
    throw error
  }
}

// Update the getMediaUploadExec function to handle validation results
export async function getMediaUploadExec(
  fileOrResult: File | PreviewResult,
  userId: string,
  options: Omit<MediaUploadOptions, "uploadType"> & { onProgress?: (progress: number) => void } = {},
): Promise<MediaUploadResult> {
  const { onProgress, ...restOptions } = options

  // Prepare the file (convert, resize, compress) if not already processed
  const {
    file: processedFile,
    dimensions,
    valid,
    error,
  } = await prepareFileForUpload(fileOrResult, userId, restOptions)

  // If validation failed, throw an error
  if (!valid) {
    throw new Error(error || "Invalid media file")
  }

  // Report initial progress (20% for file preparation)
  onProgress?.(20)

  // Get a signed URL for upload
  const { uploadUrl, filename, key } = await getMediaUploadAuth(processedFile, userId)

  // Report progress after getting signed URL (30%)
  onProgress?.(30)

  // Upload the file directly using the provided signed URL with progress tracking
  const uploadResponse = await uploadWithProgress(uploadUrl, processedFile, (uploadProgress) => {
    // Map upload progress from 0-100 to 30-90 range (30% for prep, 60% for upload, 10% for finalization)
    const mappedProgress = 30 + Math.round(uploadProgress * 0.6)
    onProgress?.(mappedProgress)
  })

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`)
  }

  // Report progress after successful upload (90%)
  onProgress?.(90)

  // Return success with the public URL
  const publicUrl = `${process.env.NEXT_PUBLIC_CDN}/${key}`

  // Report completion (100%)
  onProgress?.(100)

  return {
    success: true,
    filename,
    publicUrl,
    dimensions,
  }
}

// Helper function to upload with progress tracking
async function uploadWithProgress(url: string, file: File, onProgress?: (progress: number) => void): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open("PUT", url, true)
    xhr.setRequestHeader("Content-Type", file.type)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        onProgress?.(percentComplete)
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Create a Response object to match fetch API
        const response = new Response(null, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: new Headers({
            "Content-Type": xhr.getResponseHeader("Content-Type") || "",
          }),
        })
        resolve(response)
      } else {
        reject(new Error(`HTTP Error: ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      reject(new Error("Network Error"))
    }

    xhr.send(file)
  })
}

export function getNewFileName(perfilId: string, originalFilename: string, forceDifferentRandom = false) {
  const now = new Date()
    const dateStr = now.toISOString().replace(/[-:]/g, "").split(".")[0]!.replace("T", "_")

  if (originalFilename.includes("poster")) {
    return originalFilename
  }

  // Generate a random number only if forceDifferentRandom is true
  const randomPart = forceDifferentRandom ? `_${Math.floor(1000 + (Date.now() % 9000))}` : ""

  // Check if the file has an extension
  const hasExtension = originalFilename.includes(".")

  if (hasExtension) {
    // Split the filename and extension
    const lastDotIndex = originalFilename.lastIndexOf(".")
    const filename = originalFilename.substring(0, lastDotIndex)
    const extension = originalFilename.substring(lastDotIndex + 1).toLowerCase()

    // Use our new MediaTypeInfo to determine if we should convert to WebP
    const mediaTypeInfo = getMediaTypeInfoByExtension(extension)

    // Determine the final extension based on the media type
    let finalExtension = extension

    if (mediaTypeInfo) {
      // If it's an image that should be converted to WebP
      if (mediaTypeInfo.convertToWebp) {
        finalExtension = "webp"
      }
    } else if (IMAGE_EXTENSIONS.includes(extension)) {
      // Fallback for any image extension not explicitly defined
      finalExtension = "webp"
    }

    return `${cleanID(perfilId)}/${dateStr}${randomPart}-${filename}.${finalExtension}`
  } else {
    // No extension, assume it's an image and add .webp
    return `${cleanID(perfilId)}/${dateStr}${randomPart}-${originalFilename}.webp`
  }
}

// Generate the poster filename with the required format
export function generatePosterFilename(perfilId: string, originalFilename: string, forceDifferentRandom = false) {
  const now = new Date()
  const dateStr = now.toISOString().replace(/[-:]/g, "").split(".")[0]!.replace("T", "_")

  // Generate a random number only if forceDifferentRandom is true
  const randomPart = forceDifferentRandom ? `_${Math.floor(1000 + (Date.now() % 9000))}` : ""

  // Check if the file has an extension
  const hasExtension = originalFilename.includes(".")

  if (hasExtension) {
    // Split the filename and extension
    const lastDotIndex = originalFilename.lastIndexOf(".")
    const filename = originalFilename.substring(0, lastDotIndex)
    const extension = originalFilename.substring(lastDotIndex + 1).toLowerCase()

    // Use our new MediaTypeInfo to determine if we should convert to WebP
    const mediaTypeInfo = getMediaTypeInfoByExtension(extension)

    // Determine the final extension based on the media type
    let finalExtension = extension

    if (mediaTypeInfo) {
      // If it's an image that should be converted to WebP
      if (mediaTypeInfo.convertToWebp) {
        finalExtension = "webp"
      }
    } else if (IMAGE_EXTENSIONS.includes(extension)) {
      // Fallback for any image extension not explicitly defined
      finalExtension = "webp"
    }

    return `${cleanID(perfilId)}/poster/${dateStr}${randomPart}_${filename}.webp`
  } else {
    // No extension, assume it's an image and add .webp
    return `${cleanID(perfilId)}/poster/${dateStr}${randomPart}_${originalFilename}.webp`
  }
}

// // Re-export the improved video thumbnail generator
// export { generateVideoThumbnail, getThumbnail } from "@/app/lib/utils/video-thumbnail"

export function getPosterFilename(videoFilename: string): string {
  // Find the first slash
  

  const firstSlashIndex = videoFilename.indexOf("/")



  if (firstSlashIndex === -1) {
    // If there's no slash, just prepend "poster/" to the filename
    return `poster/${videoFilename.split(".")[0]}.webp`
  }

  // Insert "poster/" after the first slash
  const perfilIdPart = videoFilename.substring(0, firstSlashIndex + 1)
  const restOfPath = videoFilename.substring(firstSlashIndex + 1)

  // Change the extension to webp
  const filenameWithoutExt = restOfPath.split(".")[0]

  return `${perfilIdPart}poster/${filenameWithoutExt}.webp`
}

export function cleanID(text: string): string {
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
}

